import { Helmet } from "react-helmet-async";

import { useTranslation } from 'react-i18next';
const SITE_URL = "https://maxemployee.com";
const DEFAULT_OG_IMAGE_WEBP = `${SITE_URL}/og-image-homepage.webp`;
const DEFAULT_OG_IMAGE_PNG = `${SITE_URL}/og-image-homepage.png`;

// Convert a PNG og:image URL to WebP if a .webp version exists
function toWebP(url) {
  if (!url) return DEFAULT_OG_IMAGE_WEBP;
  // If it's already a .webp, return as-is
  if (url.endsWith(".webp")) return url;
  // If it's a local path with .png, try .webp equivalent
  if (url.startsWith("/") && url.endsWith(".png")) {
    return url.replace(/\.png$/, ".webp");
  }
  // For external URLs (e.g. Unsplash), return as-is (can't convert)
  return url;
}

export default function SEOHead({
  title,
  description,
  image,
  url,
  type = "website",
  structuredData,
  publishedTime,
  modifiedTime,
  tags,
  twitterCreator = "@maxemployee",
  ogLocale = "en_US",
  noIndex = false,
}) {
  const { t } = useTranslation();
  const canonicalUrl = url ? `${SITE_URL}${url}` : undefined;
  const fullTitle = title.includes("Max Employee") ? title : `${title} | Max Employee`;
  const ogImageWebP = toWebP(image);
  const ogImageFallback = image || DEFAULT_OG_IMAGE_PNG;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {/* WebP primary og:image */}
      <meta property="og:image" content={ogImageWebP} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      {/* PNG fallback for platforms with limited WebP support */}
      {ogImageWebP !== ogImageFallback && (
        <meta property="og:image" content={ogImageFallback} />
      )}
      <meta property="og:type" content={type} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:site_name" content="Max Employee" />
      <meta property="og:locale" content={ogLocale} />

      {/* Article-specific OG tags */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && tags && tags.length > 0 && (
        <>
          {tags.map((tag, i) => (
            <meta key={i} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image || DEFAULT_OG_IMAGE_WEBP} />
      <meta name="twitter:site" content="@maxemployee" />
      <meta name="twitter:creator" content={twitterCreator} />

      {/* Facebook / WhatsApp OG */}
      <meta property="og:see_also" content="https://twitter.com/maxemployee" />

      {/* Pinterest */}
      <meta name="pinterest-rich-pin" content="true" />

      {/* Additional SEO */}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />
      <meta name="theme-color" content="#09090B" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />

      {/* Structured Data */}
      {structuredData && (Array.isArray(structuredData) ? structuredData : [structuredData]).map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
